import { useMemo } from 'react'
import { cn } from '@app/lib/cn'
import { rotate } from '../../../worker/lib/yield'
import { interiorPoint } from './geometry'
import type { LayoutInstance, LayoutPlacement, MaterialLayout } from './live-calc'

/**
 * One material's placed layout, drawn true: the viewBox IS the material in
 * millimetres, every placement is redrawn exactly as `validateLayout`
 * reconstructs it (rotate, then translate), and the fire is not a number — it
 * is the bare kağıt paper visibly left empty between the pieces.
 *
 * The material is laid HORIZONTALLY: its length runs along x because a card is
 * wider than it is tall. The placer's frame has the width on x and the length
 * growing on y, so a point `(sx, sy)` lands on screen at `(sy, W − sx)` — a
 * proper rotation of the physical strip, never a mirror: chirality is the one
 * thing this drawing exists to show (aynalı çift means the mate is flipped),
 * so the mapping must not flip anything itself.
 *
 * `PlacedSvg` is the shared drawing — the strip (`StripSvg`) and the zarf
 * envelope (`zarf-svg.tsx`) are the same picture over different frames, and
 * one renderer is what keeps their conventions from drifting apart.
 *
 * Purely presentational: everything it knows arrives in props, everything it
 * does goes out through the two callbacks. Colors are CSS variables — this
 * SVG sits on the UI surface, unlike the scan canvas, so it follows the theme.
 */

export interface PlacedSvgProps {
  /** The frame's width, mm — the strip's fixed width, or the zarf envelope's. */
  widthMm: number
  /** The frame's occupied length, mm. */
  lengthMm: number
  /**
   * Where the frame's lower-left corner sits in the PLACEMENTS' coordinates,
   * `[x, y]` mm. The strip's is (0, 0) — its frame is the strip itself. The
   * zarf's is `EnvelopeResult.originMm`, a sub-cell remainder the placer keeps
   * so the layout stays on its own grid; drawing that frame from (0, 0) would
   * shift the paper off the pieces by up to one cell.
   */
  originMm?: readonly [number, number] | undefined
  placements: readonly LayoutPlacement[]
  instances: readonly LayoutInstance[]
  ariaLabel: string
  /** Set-wide request index of the selected piece, or null. */
  selectedPiece: number | null
  hoverPiece?: number | null
  /** Numeral per request piece index — the rail's own numbering. */
  pieceLabels?: readonly string[] | undefined
  onSelect?: ((pieceIndex: number) => void) | undefined
  onHover?: ((pieceIndex: number | null) => void) | undefined
  className?: string | undefined
}

export interface StripSvgProps {
  layout: MaterialLayout
  selectedPiece: number | null
  hoverPiece?: number | null
  pieceLabels?: readonly string[]
  onSelect?: (pieceIndex: number) => void
  onHover?: (pieceIndex: number | null) => void
  className?: string
}

/** On-screen height of every drawn layout — one scale rule for all materials. */
const STRIP_RENDER_HEIGHT_PX = 144

interface DrawnPlacement {
  pieceIndex: number
  d: string
  anchor: readonly [number, number] | null
}

export function StripSvg({
  layout,
  selectedPiece,
  hoverPiece = null,
  pieceLabels,
  onSelect,
  onHover,
  className,
}: StripSvgProps) {
  return (
    <PlacedSvg
      widthMm={layout.stripWidthMm}
      lengthMm={layout.usedLengthMm}
      placements={layout.placements}
      instances={layout.instances}
      ariaLabel={`Şerit yerleşimi — ${layout.placements.length} parça`}
      selectedPiece={selectedPiece}
      hoverPiece={hoverPiece}
      pieceLabels={pieceLabels}
      onSelect={onSelect}
      onHover={onHover}
      className={className}
    />
  )
}

export function PlacedSvg({
  widthMm,
  lengthMm,
  originMm,
  placements,
  instances,
  ariaLabel,
  selectedPiece,
  hoverPiece = null,
  pieceLabels,
  onSelect,
  onHover,
  className,
}: PlacedSvgProps) {
  const width = widthMm
  const length = Math.max(lengthMm, 1)
  const [originX, originY] = originMm ?? [0, 0]
  // The frame in SCREEN coordinates, under the same (sx, sy) → (sy, W − sx)
  // rotation the placements go through: its length runs from `originY`, and its
  // far edge `originX + widthMm` maps to screen y = −originX.
  const frameX = originY
  const frameY = 0 - originX

  const drawn = useMemo<DrawnPlacement[]>(() => {
    return placements.map((placement) => {
      const instance = instances[placement.instance]
      if (!instance) return { pieceIndex: -1, d: '', anchor: null }
      const ring = rotate(unpack(instance.outlineMm), placement.rotDeg)
      const screen: [number, number][] = ring.map(([x, y]) => [
        y + placement.yMm,
        width - (x + placement.xMm),
      ])
      return {
        pieceIndex: instance.pieceIndex,
        d: pathOf(screen),
        anchor: interiorPoint(screen),
      }
    })
  }, [placements, instances, width])

  // Sized against the frame so the numerals keep their proportion whatever
  // the rendered width is; the frame is always `width` mm tall.
  const fontSize = width / 16

  // Rendered size is HEIGHT-driven: the frame is a fixed `width` mm tall in
  // the world, so it gets a fixed height on screen and its length grows to the
  // right (the caller scrolls it horizontally when a set runs long).
  // Width-driven sizing inverts that — a short strip becomes a tower of empty
  // paper filling the card with fire that isn't there.
  const renderWidth = Math.max(1, Math.round((STRIP_RENDER_HEIGHT_PX * length) / width))

  return (
    <svg
      viewBox={`${round(frameX)} ${round(frameY)} ${round(length)} ${round(width)}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      className={cn('block shrink-0', className)}
      style={{ height: STRIP_RENDER_HEIGHT_PX, width: renderWidth }}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
    >
      {/* The material itself: bare paper framed by a hairline. What stays paper is fire. */}
      <rect
        x={round(frameX)}
        y={round(frameY)}
        width={round(length)}
        height={round(width)}
        fill="var(--color-kagit)"
        stroke="var(--color-hairline)"
        vectorEffect="non-scaling-stroke"
      />
      {drawn.map((placement, at) =>
        placement.pieceIndex < 0 ? null : (
          <path
            key={at}
            data-placement={at}
            data-piece={placement.pieceIndex}
            d={placement.d}
            fill={
              placement.pieceIndex === selectedPiece
                ? 'color-mix(in srgb, var(--color-kordovan) 12%, transparent)'
                : 'color-mix(in srgb, var(--color-ink) 8%, transparent)'
            }
            stroke={
              placement.pieceIndex === selectedPiece
                ? 'var(--color-kordovan)'
                : 'var(--color-ink-muted)'
            }
            strokeWidth={
              placement.pieceIndex === selectedPiece || placement.pieceIndex === hoverPiece
                ? 1.5
                : 1
            }
            vectorEffect="non-scaling-stroke"
            className={onSelect ? 'cursor-pointer' : undefined}
            onClick={onSelect ? () => onSelect(placement.pieceIndex) : undefined}
            onMouseEnter={onHover ? () => onHover(placement.pieceIndex) : undefined}
          />
        ),
      )}
      {drawn.map((placement, at) =>
        placement.anchor === null || placement.pieceIndex < 0 ? null : (
          <text
            key={`n${at}`}
            x={round(placement.anchor[0])}
            y={round(placement.anchor[1])}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={fontSize}
            fontFamily="var(--font-sans)"
            fontWeight={600}
            fill={
              placement.pieceIndex === selectedPiece
                ? 'var(--color-kordovan)'
                : 'var(--color-ink-muted)'
            }
            pointerEvents="none"
          >
            {pieceLabels?.[placement.pieceIndex] ?? String(placement.pieceIndex + 1)}
          </text>
        ),
      )}
    </svg>
  )
}

function unpack(outline: Float32Array): [number, number][] {
  const points: [number, number][] = new Array(outline.length / 2)
  for (let i = 0; i < points.length; i += 1) {
    points[i] = [outline[2 * i] as number, outline[2 * i + 1] as number]
  }
  return points
}

function pathOf(ring: readonly (readonly [number, number])[]): string {
  if (ring.length === 0) return ''
  let d = `M${round(ring[0]![0])} ${round(ring[0]![1])}`
  for (let i = 1; i < ring.length; i += 1) {
    d += `L${round(ring[i]![0])} ${round(ring[i]![1])}`
  }
  return `${d}Z`
}

/** Two decimals — a hundredth of a millimetre is far below anything visible. */
function round(value: number): number {
  return Math.round(value * 100) / 100
}
